import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { MessageSquare, Send, Paperclip } from "lucide-react";
import Comments from "../comments";
import UploadAttachment from "../uploadAttachment";
import { Spinner } from "./CommonComponents";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsCommentsSection = () => {
    const {
        itemType,
        itemComments,
        filterComment,
        handleFilterCommentChange,
        handleCommentModify,
        getFilteredComments,
        comment,
        handleCommentChange,
        saveComment,
        savingComment,
        onFileUpload,
    } = useItemDetailsContext();

    const commentsCount = itemComments?.length || 0;

    return (
        <div className="space-y-4">
            {/* Section Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Comments</span>
                    {commentsCount > 0 && (
                        <Badge variant="secondary" className="text-xs">
                            {commentsCount}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Comments List */}
            {itemComments && itemComments.length > 0 && (
                <Card className="py-0 overflow-hidden">
                    <CardContent className="p-4">
                        <Comments
                            comments={getFilteredComments()}
                            filterComponent={handleFilterCommentChange}
                            filterEnabled={filterComment}
                            tableName={itemType}
                            onModification={handleCommentModify}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Empty State */}
            {(!itemComments || itemComments.length === 0) && (
                <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-lg bg-muted/30">
                    <MessageSquare className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">No comments yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Be the first to add a comment</p>
                </div>
            )}

            {/* Comment Input */}
            <Card className="py-0 overflow-hidden">
                <CardContent className="p-3">
                    {savingComment ? (
                        <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground animate-pulse">
                            <Spinner size={16} />
                            <span>Saving comment...</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Input
                                placeholder="Write a comment..."
                                value={comment}
                                spellCheck={false}
                                onKeyPress={saveComment}
                                onChange={handleCommentChange}
                                className="border-0 shadow-none focus-visible:ring-0 bg-transparent"
                            />
                            <div className="flex items-center gap-1">
                                <UploadAttachment onFileChange={onFileUpload} />
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={(e) => {
                                                if (comment.trim()) {
                                                    saveComment({ key: "Enter" });
                                                }
                                            }}
                                            disabled={!comment.trim()}
                                        >
                                            <Send className={cn(
                                                "h-4 w-4 transition-colors",
                                                comment.trim() ? "text-primary" : "text-muted-foreground"
                                            )} />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {comment.trim() ? "Send comment (Enter)" : "Type a comment first"}
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            
            <p className="text-xs text-muted-foreground text-center">
                Press Enter to send • Supports attachments
            </p>
        </div>
    );
};

export default ItemDetailsCommentsSection;
